/**
 * Centralized service for managing user preferences
 * Syncs preferences between localStorage and the database
 */

import { getAuthHeaders } from './authService';

const API_BASE_URL = 'https://apilinkoo.creago.be/rest/linkoo/peoples';

// Debounce timer for saving preferences
let saveDebounceTimer = null;
const SAVE_DEBOUNCE_MS = 1000;

// In-memory cache of preferences
let preferencesCache = null;

/**
 * Get current user ID from localStorage
 */
const getUserId = () => {
    return parseInt(localStorage.getItem('userId'), 10) || 0;
};

/**
 * Get all preferences from API
 * @returns {Promise<Object>} Preferences object
 */
export const getPreferencesFromApi = async () => {
    const userId = getUserId();
    if (!userId) return null;

    try {
        const response = await fetch(`${API_BASE_URL}/${userId}/preferences`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();
        const success = result.success || result.SUCCESS;
        const data = result.data || result.DATA || {};

        if (success) {
            // Normalize keys
            const prefs = {
                pinnedItems: data.pinnedItems || data.pinneditems || data.PINNEDITEMS || [],
                selectedThemeId: data.selectedThemeId || data.selectedthemeid || data.SELECTEDTHEMEID || 0,
                collapsedSidebarGroups: data.collapsedSidebarGroups || data.collapsedsidebargroups || data.COLLAPSEDSIDEBARGROUPS || {},
                language: data.language || data.LANGUAGE || 'fr'
            };
            preferencesCache = prefs;
            return prefs;
        }

        return null;
    } catch (error) {
        console.error('Error fetching preferences from API:', error);
        return null;
    }
};

/**
 * Save preferences to API (debounced)
 * @param {Object} preferences - Preferences to save (partial update supported)
 */
export const savePreferencesToApi = (preferences) => {
    const userId = getUserId();
    if (!userId) return;

    // Clear existing timer
    if (saveDebounceTimer) {
        clearTimeout(saveDebounceTimer);
    }

    // Set new timer
    saveDebounceTimer = setTimeout(async () => {
        try {
            console.log('Saving preferences to API:', preferences);
            const response = await fetch(`${API_BASE_URL}/${userId}/preferences`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                credentials: 'include',
                body: JSON.stringify(preferences)
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const result = await response.json();
            console.log('Preferences saved:', result);
        } catch (error) {
            console.error('Error saving preferences to API:', error);
        }
    }, SAVE_DEBOUNCE_MS);
};

/**
 * Save preferences immediately (no debounce)
 * @param {Object} preferences - Preferences to save
 */
export const savePreferencesImmediately = async (preferences) => {
    const userId = getUserId();
    if (!userId) return false;

    try {
        const response = await fetch(`${API_BASE_URL}/${userId}/preferences`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(preferences)
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }

        return true;
    } catch (error) {
        console.error('Error saving preferences to API:', error);
        return false;
    }
};

/**
 * Get cached preferences or fetch from API
 */
export const getPreferences = async () => {
    if (preferencesCache) {
        return preferencesCache;
    }
    return getPreferencesFromApi();
};

/**
 * Clear preferences cache (call on logout)
 */
export const clearPreferencesCache = () => {
    preferencesCache = null;
    if (saveDebounceTimer) {
        clearTimeout(saveDebounceTimer);
        saveDebounceTimer = null;
    }
};

// =====================
// Theme Preferences
// =====================

/**
 * Get selected theme ID from preferences
 */
export const getSelectedThemeId = async () => {
    const prefs = await getPreferences();
    return prefs?.selectedThemeId || 0;
};

/**
 * Save selected theme ID
 */
export const saveSelectedThemeId = (themeId) => {
    savePreferencesToApi({ selectedThemeId: themeId });
};

// =====================
// Language Preferences
// =====================

/**
 * Get language from preferences
 */
export const getLanguagePreference = async () => {
    const prefs = await getPreferences();
    return prefs?.language || 'fr';
};

/**
 * Save language preference
 */
export const saveLanguagePreference = (language) => {
    savePreferencesToApi({ language });
};

// =====================
// Sidebar Preferences
// =====================

/**
 * Get collapsed sidebar groups from preferences
 */
export const getCollapsedSidebarGroups = async () => {
    const prefs = await getPreferences();
    return prefs?.collapsedSidebarGroups || {};
};

/**
 * Save collapsed sidebar groups
 */
export const saveCollapsedSidebarGroups = (groups) => {
    savePreferencesToApi({ collapsedSidebarGroups: groups });
};

// =====================
// Pinned Items
// =====================

/**
 * Get pinned items from preferences
 */
export const getPinnedItems = async () => {
    const prefs = await getPreferences();
    return prefs?.pinnedItems || [];
};

/**
 * Save pinned items
 */
export const savePinnedItems = (items) => {
    savePreferencesToApi({ pinnedItems: items });
};
